import React from 'react';
import SEO from '../../components/common/SEO';
import { buildCanonicalUrl } from '../../utils/seoUtils';

const CookiePolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Cookie Policy"
        description="Learn about how KL Accelerators uses cookies and similar technologies to improve your experience on our enterprise accelerator platform."
        canonicalUrl={buildCanonicalUrl('/cookie-policy')}
        keywords="cookie policy, cookies, tracking, KL Accelerators"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Cookie Policy</h1>
          <div className="text-sm text-gray-600 mb-8">
            <p><strong>Last Updated:</strong> December 2024</p>
            <p><strong>Effective Date:</strong> January 1, 2025</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. What Are Cookies</h2>
            <p className="text-gray-700 mb-4">
              Cookies are small text files stored on your device when you visit our website. They help us provide 
              a better user experience, analyze website usage, and remember your preferences.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Types of Cookies We Use</h2>
            
            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">2.1 Essential Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies are necessary for our website to function properly and cannot be disabled:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Session cookies:</strong> Maintain your session while browsing</li>
              <li><strong>Security cookies:</strong> Protect against fraud and secure your account</li>
              <li><strong>Load balancing cookies:</strong> Distribute traffic across our servers</li>
              <li><strong>Authentication cookies:</strong> Remember your login status</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">2.2 Performance Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies help us understand how visitors interact with our website:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Google Analytics:</strong> Track website usage and performance</li>
              <li><strong>Error tracking:</strong> Identify and fix technical issues</li>
              <li><strong>Speed monitoring:</strong> Optimize page load times</li>
              <li><strong>Feature usage:</strong> Understand which features are most valuable</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">2.3 Functional Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies enhance your experience by remembering your preferences:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Language preferences:</strong> Remember your chosen language</li>
              <li><strong>Theme settings:</strong> Store your display preferences</li>
              <li><strong>Form data:</strong> Save partially completed forms</li>
              <li><strong>Filter settings:</strong> Remember your search and filter preferences</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">2.4 Marketing Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies help us provide relevant content and measure marketing effectiveness:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Advertising cookies:</strong> Show relevant ads on other websites</li>
              <li><strong>Social media cookies:</strong> Enable social sharing features</li>
              <li><strong>Campaign tracking:</strong> Measure the success of marketing campaigns</li>
              <li><strong>Personalization:</strong> Customize content based on your interests</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. Third-Party Cookies</h2>
            <p className="text-gray-700 mb-4">
              We use several third-party services that may set their own cookies:
            </p>
            
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Google Services</h4>
              <ul className="list-disc pl-6 text-gray-700">
                <li><strong>Google Analytics:</strong> Website traffic analysis</li>
                <li><strong>Google Tag Manager:</strong> Cookie and tag management</li>
                <li><strong>Google Ads:</strong> Advertising and remarketing</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Social Media Platforms</h4>
              <ul className="list-disc pl-6 text-gray-700">
                <li><strong>LinkedIn:</strong> Professional networking integration</li>
                <li><strong>Twitter:</strong> Social sharing capabilities</li>
                <li><strong>Facebook:</strong> Social login and sharing</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Customer Support</h4>
              <ul className="list-disc pl-6 text-gray-700">
                <li><strong>Intercom:</strong> Live chat and customer support</li>
                <li><strong>Zendesk:</strong> Help desk and ticketing system</li>
              </ul>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Cookie Duration</h2>
            <p className="text-gray-700 mb-4">Cookies have different lifespans:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
              <li><strong>Persistent cookies:</strong> Remain until expiration date or manual deletion</li>
              <li><strong>Essential cookies:</strong> Typically expire after 24 hours</li>
              <li><strong>Analytics cookies:</strong> Usually expire after 2 years</li>
              <li><strong>Marketing cookies:</strong> Generally expire after 30-90 days</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Managing Your Cookie Preferences</h2>
            
            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">5.1 Browser Settings</h3>
            <p className="text-gray-700 mb-4">
              You can control cookies through your browser settings:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
              <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
              <li><strong>Edge:</strong> Settings → Cookies and Site Permissions</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">5.2 Our Cookie Consent Tool</h3>
            <p className="text-gray-700 mb-4">
              When you first visit our site, you'll see a cookie consent banner where you can:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Accept all cookies</li>
              <li>Reject non-essential cookies</li>
              <li>Customize your preferences by category</li>
              <li>Change your preferences at any time</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">5.3 Opt-Out Links</h3>
            <p className="text-gray-700 mb-4">
              You can opt out of specific tracking services:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><a href="https://tools.google.com/dlpage/gaoptout" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a></li>
              <li><a href="https://optout.aboutads.info/" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance Opt-out</a></li>
              <li><a href="https://optout.networkadvertising.org/" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Network Advertising Initiative Opt-out</a></li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Impact of Disabling Cookies</h2>
            <p className="text-gray-700 mb-4">
              Disabling certain cookies may affect your experience:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Essential cookies:</strong> Website may not function properly</li>
              <li><strong>Performance cookies:</strong> We can't improve our services based on usage data</li>
              <li><strong>Functional cookies:</strong> You'll need to re-enter preferences each visit</li>
              <li><strong>Marketing cookies:</strong> You may see less relevant advertisements</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Updates to This Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Cookie Policy to reflect changes in our practices or applicable laws. 
              We'll notify you of significant changes through our website or email.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about our use of cookies, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700"><strong>Email:</strong> contact@kadellabs.com</p>
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Need to Update Your Cookie Preferences?</h3>
              <p className="text-blue-800 mb-3">
                You can change your cookie settings at any time by clicking the "Cookie Preferences" 
                link in our website footer or contacting us directly.
              </p>
              <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors">
                Manage Cookie Preferences
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;